import React, { Component } from 'react';
import { makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import { Activity } from '@AppConstants';
import { PermissionType, PositioningDowngradeRefundType, RuleGroupItem, SalaryDailyViewModelResponseItemExt, SalaryItemType, SalaryItemUnit, SalaryPeriodResponseItem, SalaryPeriodStatus } from '@Models';
import { DateTimeService, securityService, stringSearchService } from '@Services';
import { modalService } from '@Components/Modal/ModalService';

import { DutyTableStore } from './DutyTableStore';
import { DataGrid, DataGridColumnDef, DataFieldType, ModalButtonType, NotificationHandler } from '@Components';
import { CellChangingArgs, CorrectionCell } from './Components/CorrectionCell';
import { CorrectionResult, UpdateCorrectionDialog, UpdateCorrectionDialogProps } from './Dialogs/UpdateCorrectionDialog';
import DutyContextMenu from './Components/DutyContextMenu';
import { FilterSectionStore } from '@Pages/Home/Components';
import './duty-table.scss';

type DutyTableProps = {
    period: SalaryPeriodResponseItem;
    personId: string;
    filterStore: FilterSectionStore;
    onLoading: (personId: string, operation: PromiseLike<unknown>) => void;
    onPersonUpdated: (personId: string) => void;
    onResizeHeader?: (width?: number, height?: number) => void;
    headerHeight?: number;
};

@observer
class DutyTable extends Component<DutyTableProps> {
    @observable.ref private _dutyListRef: React.RefObject<HTMLDivElement> = React.createRef();
    @observable private _ctrlPressed = false;
    private _store: DutyTableStore = new DutyTableStore();

    constructor(props: DutyTableProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        void this.refresh();
        document.addEventListener('keydown', this._onKeyChange);
        document.addEventListener('keyup', this._onKeyChange);
        window.addEventListener('blur', this._onWindowBlur);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this._onKeyChange);
        document.removeEventListener('keyup', this._onKeyChange);
        window.removeEventListener('blur', this._onWindowBlur);
    }

    componentDidUpdate(prevProps: DutyTableProps) {
        if (prevProps.period.id !== this.props.period.id || prevProps.personId !== this.props.personId) {
            void this.refresh();
        }
    }

    private _onKeyChange = (e: KeyboardEvent) => {
        if (e.ctrlKey === this._ctrlPressed) return;
        runInAction(() => { this._ctrlPressed = e.ctrlKey; });
    };

    private _onWindowBlur = () => {
        if (!this._ctrlPressed) return;
        runInAction(() => { this._ctrlPressed = false; });
    };

    render() {
        const { onResizeHeader, headerHeight } = this.props;
        const { dataSource, groups } = this._store;
        const totalRowContent = groups.length ? this._computeTotalRow() : undefined;

        return (
            <>
                <div ref={this._dutyListRef} className={this._ctrlPressed ? 'ctrl-pressed' : undefined} style={{ height: '100%' }}>
                    <DataGrid<SalaryDailyViewModelResponseItemExt>
                        tableKey="duty-table"
                        idField="date"
                        columns={this._columns}
                        items={dataSource}
                        initialItemsList={dataSource}
                        filteredItemsWithoutColumnFilters={dataSource}
                        maxHeight="100%"
                        totalRow={totalRowContent}
                        loading={this._store.loading}
                        disableSelection
                        dynamicRowHeight
                        getRowAttributes={(item) => ({ 'data-menu-date': item.date?.toString() ?? '' })}
                        headerHeight={headerHeight}
                        onHeaderHeightChange={(h) => onResizeHeader?.(undefined, h)}
                    />
                </div>
                <DutyContextMenu container={this._dutyListRef} personId={this.props.personId} />
            </>
        );
    }

    private get _columns(): DataGridColumnDef<SalaryDailyViewModelResponseItemExt>[] {
        const { groups } = this._store;
        return [
            {
                title: 'Date',
                dataField: 'date',
                dataFieldType: DataFieldType.Date,
                renderCell: (_value, item) => <>{DateTimeService.toUiDate(item.date)}</>,
                minWidth: '70px'
            },
            {
                title: 'Duty',
                dataField: 'duty',
                renderCell: (_value, item) => this._renderDutyCell(item),
                minWidth: '150px'
            },
            {
                title: 'Routing',
                dataField: 'routing'
            },
            {
                title: 'BH',
                dataField: 'blockHours1DaySec',
                dataFieldType: DataFieldType.Time,
                renderCell: (_value, item) => <>{item.blockHours1DaySec ? DateTimeService.toUiSeconds(item.blockHours1DaySec) : ''}</>
            },
            ...groups.map(rg => ({
                title: rg.importType ?? rg.name ?? '',
                dataField: `ruleGroupId_${rg.id}` as keyof SalaryDailyViewModelResponseItemExt,
                dataFieldType: DataFieldType.Number,
                renderHeader: () => <>{rg.importType ?? rg.name}</>,
                renderSubTitle: () => <small>{rg.sapAccount}, {rg.unit === SalaryItemUnit.Money ? 'CHF' : rg.unit}</small>,
                getFilterOptionName: (item: SalaryDailyViewModelResponseItemExt) => {
                    const val = item[`ruleGroupId_${rg.id}`];
                    return typeof val === 'number' ? val.toFixed(2) : '';
                },
                renderCell: (_value: unknown, item: SalaryDailyViewModelResponseItemExt) => this._renderCorrectionCell(rg, item),
                className: 'correction-cell'
            } as DataGridColumnDef<SalaryDailyViewModelResponseItemExt>))
        ];
    }

    private _renderDutyCell(item: SalaryDailyViewModelResponseItemExt): React.ReactNode {
        const { duties, remarks, dutyHours1DaySec, actualBlockHours1DaySec, releasedDuty, duty } = item;

        let cellText = duty ?? '';
        const flags: JSX.Element[] = [];
        let hasLongHaul = false;
        let hasIntroduction = false;

        const checkerTrainees: string[] = [];
        const downgradeRefunds: PositioningDowngradeRefundType[] = [];
        for (let i = 0; i < duties.length; i++) {
            const d = duties[i];
            if (d.checkerTraineeInfos) {
                for (const ct of d.checkerTraineeInfos) {
                    let ctText = `${ct.isChecker ? 'Checker' : 'Trainee'} ${ct.functionName?.toLocaleUpperCase()}`;
                    ctText += ct.instructorQualification ? `-${ct.instructorQualification}` : '';
                    ctText += ct.relatedPersonCode ? ` with ${ct.relatedPersonCode}` : '';
                    if (!checkerTrainees.includes(ctText)) checkerTrainees.push(ctText);
                }
            }
            if (d.positioningDowngradeRefundType && d.positioningDowngradeRefundType !== PositioningDowngradeRefundType.None) {
                const refundType = d.positioningDowngradeRefundType;
                if (!downgradeRefunds.includes(refundType)) downgradeRefunds.push(refundType);
            }
            if (d.activityCodeName === Activity.SUPPORT) flags.push(<span key={`support-${i}`} className="flag flag-support">S</span>);
            if (d.activityCodeName === Activity.PIKETT) flags.push(<span key={`pikett-${i}`} className="flag flag-pikett">P</span>);
            if (d.isLongHaul) hasLongHaul = true;
            if (d.isIntroduction) hasIntroduction = true;
        }

        if (checkerTrainees.length) cellText += ' / ' + checkerTrainees.join(' / ');
        if (downgradeRefunds.length) cellText += ' / ' + downgradeRefunds.join(' / ');

        const dutyRemarks = duties.filter(x => !!x.remarks).map(x => x.remarks).join(' / ');
        if (dutyRemarks) cellText += ' / ' + dutyRemarks;
        if (dutyHours1DaySec) cellText += ' / DH: ' + DateTimeService.toUiSeconds(dutyHours1DaySec);
        if (actualBlockHours1DaySec) cellText += ' / BH: ' + DateTimeService.toUiSeconds(actualBlockHours1DaySec);
        if (hasIntroduction) cellText += ' / 🧪';
        if (remarks) cellText += ' / ' + remarks.split(';').filter(x => !!x).join(' / ');

        let hint = cellText;
        if (hasLongHaul) hint += ' ✈';
        if (releasedDuty !== duty) hint += '\nReleased:\n' + releasedDuty;

        const currentUrl = new URL(window.location.href);
        let modifiedUrl = currentUrl.href.replace('vasa', 'crewpage');
        modifiedUrl = modifiedUrl.replace('https://localhost:8088', 'http://crewpage:8080');
        const cpUrl = `${modifiedUrl}?personId=${this.props.personId}&date=${item.date.toJSON()}`;

        return (
            <>
                {releasedDuty !== duty && <span className="released-indicator" title={hint} />}
                <a href={cpUrl} className="duty-link" title={hint} target="_blank" rel="noreferrer" onClick={this._onDutyClick}>
                    {this._renderText(cellText)}
                </a>
                {flags}
            </>
        );
    }

    private _onDutyClick = (e: React.MouseEvent) => {
        if (!e.ctrlKey) e.preventDefault();
    };

    private _renderCorrectionCell(rg: RuleGroupItem, item: SalaryDailyViewModelResponseItemExt): React.ReactNode {
        const { period, personId, onPersonUpdated } = this.props;
        const { date, items } = item;
        const itemsSum = item[`ruleGroupId_${rg.id}`];

        const correction = items.find(x => x.ruleGroupId === rg.id && x.sapAccount === rg.sapAccount && x.unit === rg.unit && x.type === SalaryItemType.ManualCorrection);
        const itemsWithoutCorrection = typeof correction?.amount === 'number' ? (itemsSum ?? 0) - correction.amount : itemsSum;

        const onChangeCorrection = (args: CellChangingArgs, onUpdate: (updatePromise: Promise<unknown>) => void): Promise<void> => {
            const inputValue = +args.value || 0;
            return (async () => {
                const { button, result } = await modalService.show<UpdateCorrectionDialogProps, CorrectionResult>(UpdateCorrectionDialog, {
                    RuleGroup: rg,
                    correction,
                    value: inputValue,
                    calculatedAmount: itemsWithoutCorrection
                });

                if (button === ModalButtonType.Save && result && typeof result.amount !== 'undefined' && result.amount !== null && result.comment) {
                    const correctionAmount = result.amount - (itemsWithoutCorrection ?? 0);
                    await this._store.updateCorrrection(period.id, personId, date, rg.id, rg.sapAccount, rg.unit, correctionAmount, result.comment, onUpdate);
                    NotificationHandler.showSuccess(`Successfully updated ${rg.name} (${rg.sapAccount})`);
                    onPersonUpdated(personId);
                    return;
                }

                if (button === ModalButtonType.Delete) {
                    await this._store.deleteCorrrection(period.id, personId, date, rg.id, rg.sapAccount, rg.unit);
                    NotificationHandler.showSuccess(`Successfully removed corection to ${rg.name} (${rg.sapAccount})`);
                    onPersonUpdated(personId);
                    return;
                }

                args.cancel = true;
            })();
        };

        const isDisabled = !securityService.hasPermission(PermissionType.SalaryManualCorrection)
            || period.status === SalaryPeriodStatus.Released
            || (period.status === SalaryPeriodStatus.ReadyToRelease && !securityService.hasPermission(PermissionType.ReleaseImportPeriod));

        return (
            <CorrectionCell
                correction={correction}
                calculatedValue={itemsWithoutCorrection}
                initialValue={itemsSum}
                onChanging={onChangeCorrection}
                disabled={isDisabled}
            />
        );
    }

    private _computeTotalRow(): React.ReactNode[] {
        const { dataSource, groups } = this._store;
        const fixedCells: React.ReactNode[] = [null, null, null, null];
        const groupCells = groups.map(rg => {
            let val = 0;
            for (const row of dataSource) {
                for (const item of row.items) {
                    if (item.ruleGroupId === rg.id && item.sapAccount === rg.sapAccount && item.unit === rg.unit && item.amount) {
                        val += item.amount;
                    }
                }
            }
            return <React.Fragment key={rg.id}>{val ? val.toFixed(2) : '-'}</React.Fragment>;
        });
        return [...fixedCells, ...groupCells];
    }

    private _renderText(text: string | undefined): React.ReactNode {
        const { searchDutyTerms, textComparer } = this.props.filterStore;
        if (!searchDutyTerms?.length) return text;
        const parts = stringSearchService.markMatches(text, searchDutyTerms, textComparer);
        return (
            <>
                {parts.map((p, index) => p.isMatch
                    ? <span className="text-match" key={'t' + index}>{p.text}</span>
                    : p.text
                )}
            </>
        );
    }

    public async refresh() {
        const operation = this._store.loadData(this.props.period.id, this.props.personId);
        this.props.onLoading(this.props.personId, operation);
        await operation;
    }
}

export { DutyTable };
